import React from 'react';
import { useTheme } from '../context/ThemeContext';
import lockup from '../assets/brand/balloon/comtev-lockup.svg';
import lockupReversed from '../assets/brand/balloon/comtev-lockup-reversed.svg';

export type BrandLogoVariant = 'horizontal' | 'stacked';
export type BrandLogoTone = 'ink' | 'orange';
export type BrandLogoSurface = 'light' | 'dark' | 'auto';

interface BrandLogoProps {
  /** Kept for call-site compatibility; the balloon lockup is horizontal only. */
  variant?: BrandLogoVariant;
  /** Kept for call-site compatibility; the balloon lockup has one colourway per surface. */
  tone?: BrandLogoTone;
  /**
   * Background the lockup sits on. `light`/`dark` are explicit (fixed-colour
   * surfaces like the cream nav or the dark footer); `auto` follows the active
   * theme. Picks the grey or bone wordmark so the lockup never washes out —
   * no `filter: invert()` hacks.
   */
  surface?: BrandLogoSurface;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  alt?: string;
  width?: number;
  height?: number;
}

// The balloon lockup is the one Comtev logo on both domains (moved over from
// comtev.ng, 2026-09-21). It ships as a single horizontal lockup in two
// colourways: grey wordmark for light surfaces, bone wordmark for dark. The
// `variant` and `tone` props are kept so existing call sites compile, but every
// combination now resolves to the same artwork; the retired "Sprout" files stay
// in src/assets/brand/svg for reference.
const ASSETS = { light: lockup, dark: lockupReversed } as const;

/**
 * The Comtev balloon lockup — the single source of truth for rendering the logo.
 * Every nav, footer, auth card, dashboard, and public-doc header goes through this
 * so the right artwork (ink / cream-reversed / orange / orange-reversed) is chosen
 * by surface + tone rather than per-page asset imports.
 */
export default function BrandLogo({
  surface = 'auto',
  className,
  style,
  onClick,
  alt = 'Comtev',
  width,
  height,
}: BrandLogoProps) {
  // `useTheme` falls back to light outside a ThemeProvider, so explicit-`surface`
  // usages (marketing/public pages) render fine without one.
  const { theme } = useTheme();
  const resolved: 'light' | 'dark' =
    surface === 'auto' ? (theme === 'dark' ? 'dark' : 'light') : surface;
  const src = ASSETS[resolved];
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      width={width}
      height={height}
      draggable={false}
    />
  );
}
